import React, { useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getInitialRange } from '../../../../.globals/helpers';

// Components
import { Loading, Icons } from "@gpstrackit/gtc-react-controls";
import { Timezone } from 'Components/uiControls';
import { Divider } from '@mui/material';

// Actions
import { openModalEditCoachPanel } from "Modules/driverPerformance/views/cases/modals/editCoachPanel/redux/slice";
import { getCase } from './redux/slice'

const CaseInfo = (props) => {
    const { caseNumber, openTooltip, driverDetailStartDate, driverDetailEndDate } = props
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { caseInfo, loading } = useSelector((state) => state.driverPerformanceEventsTabCaseInfoRedux);
    const user = useSelector(state => state.securityRedux.user);
    const { dateformat, timeZone } = user;
    const dateStart = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateStart);
    const dateEnd = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateEnd);
    const mainTab = useSelector((state) => state?.driverPerformanceMainRedux?.tab);

    const getReturnTab = () => {
        const urlParams = new URLSearchParams(location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) return tabParam;
        switch (mainTab) {
            case 0:
                return 'dashboard';
            case 2:
                return 'events';
            case 3:
                return 'cases';
            case 1:
            default:
                return 'drivers';
        }
    };

    const getRangeForDriverDetail = () => {
        if (
            driverDetailStartDate &&
            driverDetailEndDate &&
            moment(driverDetailStartDate).isValid() &&
            moment(driverDetailEndDate).isValid()
        ) {
            return {
                startDate: driverDetailStartDate,
                endDate: driverDetailEndDate
            };
        }

        const urlParams = new URLSearchParams(location.search);

        const startParam = urlParams.get('eventsStartDate') || urlParams.get('driversStartDate');
        const endParam = urlParams.get('eventsEndDate') || urlParams.get('driversEndDate');
        const rangeParam = urlParams.get('eventsRange') || urlParams.get('driversRange');

        const hasStartEndParams =
            Boolean(startParam && endParam) &&
            moment(startParam).isValid() &&
            moment(endParam).isValid();

        if (hasStartEndParams) {
            return {
                startDate: moment.utc(startParam).startOf('day').toISOString(),
                endDate: moment.utc(endParam).endOf('day').toISOString()
            };
        }

        if (rangeParam) {
            const range = getInitialRange(rangeParam);
            if (range?.start && range?.end) {
                return {
                    startDate: moment.utc(range.start).startOf('day').toISOString(),
                    endDate: moment.utc(range.end).endOf('day').toISOString()
                };
            }
        }

        if (dateStart && dateEnd) {
            return {
                startDate: moment.utc(dateStart).startOf('day').toISOString(),
                endDate: moment.utc(dateEnd).endOf('day').toISOString()
            };
        }

        return {
            startDate: moment.utc().startOf('month').toISOString(),
            endDate: moment.utc().endOf('month').toISOString()
        };
    };

    const buildDriverDetailUrl = (driverIdToOpen) => {
        const { startDate, endDate } = getRangeForDriverDetail();
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('sourceModule', 'eventsCaseInfo');
        params.append('returnTab', getReturnTab());
        return `/app/driverPerformance/driverDetail/${driverIdToOpen}?${params.toString()}`;
    };

    useEffect(() => {
        if (openTooltip) {
            dispatch(getCase({ caseNumber }))
        }
    }, [openTooltip])

    return (
        <div style={{ minHeight: 45, width: 260 }}>
            {
                loading ? <div><Loading full /></div > :
                    <>
                        {
                            caseInfo ? (
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_caseId']}:</strong>
                                        <a
                                            href={`/app/driverPerformance/caseDetail/${caseInfo?.id}`}
                                            noWrap
                                            style={{
                                                color: '#005bf8',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: 1,
                                                minWidth: 0
                                            }}
                                            onClick={(e) => { e.preventDefault(); navigate(`/app/driverPerformance/caseDetail/${caseInfo?.id}`); }} >
                                            {caseInfo?.caseNumber}
                                        </a>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_status']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.status?.label ? messages[`driver_performance_cases_status_${caseInfo.status.label?.toLowerCase()}`] || caseInfo.status.label : 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_next_step']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.status?.nextStep ? messages[`driver_performance_cases_step_${caseInfo.status.nextStep.replace(/\s/g, "")}`] || caseInfo.status.nextStep : 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_created']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.createdOn ? <Timezone date={caseInfo?.createdOn} dateFormat={dateformat} noPopover timeFormat="hh:mm a" timezone={timeZone} /> : 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_driver']}:</strong>
                                        <a
                                            href={buildDriverDetailUrl(caseInfo?.driver?.id)}
                                            noWrap
                                            style={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: 1,
                                                minWidth: 0,
                                                color: '#005bf8',
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(buildDriverDetailUrl(caseInfo?.driver?.id));
                                            }}
                                        >
                                            {caseInfo?.driver?.name}
                                        </a>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_coach']}:
                                        </strong>
                                        <p
                                            style={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: caseInfo?.coach?.firstName && 1,
                                                minWidth: 0,
                                                borderBottom: !caseInfo?.coach?.firstName && "1px dashed rgba(89, 81, 81, 0.8)",
                                                cursor: !caseInfo?.coach?.firstName && 'pointer',
                                            }}
                                            onClick={() => {
                                                if (!caseInfo?.coach?.firstName) {
                                                    dispatch(openModalEditCoachPanel({
                                                        open: true,
                                                        dataToCase: caseInfo
                                                    }))
                                                }
                                            }}
                                        >
                                            {caseInfo?.coach?.firstName || messages['driver_performance_events_info_unassigned']}
                                        </p>
                                    </div>
                                    <Divider sx={{ mt: 0.5, mb: 0.5 }} />
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_created']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.createdOn ? <Timezone
                                                date={caseInfo?.createdOn}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                                noPopover
                                            /> : <></>}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_evaluation']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.startEvaluationDate ? <Timezone
                                                date={caseInfo?.startEvaluationDate}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                                noPopover
                                            /> : <>N/A</>}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 21 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_complete']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.completedDate ? <Timezone
                                                date={caseInfo?.completedDate}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                                noPopover
                                            /> : <>N/A</>}</p>
                                        </div>
                                    </div>
                                    {
                                        caseInfo?.coach?.firstName &&
                                        <div>
                                            <Divider sx={{ mt: 0.5, mb: 0.5 }} />
                                            <div
                                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                                onClick={() => {
                                                    dispatch(openModalEditCoachPanel({
                                                        open: true,
                                                        dataToCase: caseInfo
                                                    }))
                                                }}
                                            >
                                                <Icons name="perm_identity" size="small" />
                                                <span className="driver-info-update-driver">
                                                    {messages['driver_performance_events_change_coach']}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100 }}>{messages['driver_performance_events_info_no_case_info']}</div>
                            )
                        }
                    </>
            }
        </div>
    )
}

export default CaseInfo; 